import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography,
} from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { AccountService } from '../../services/account.service';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { leafAccounts } from '../../utils/postingAccounts';
import { FixedAssetService } from '../../services/fixedAsset.service';
import { Account } from '../../interfaces/accounting.interface';
import { FixedAsset, assetCarrying } from '../../interfaces/fixedAsset/fixedAsset.interface';
import { toLocalIsoDate } from '../../utils/dateUtils';
import { helpWhy } from '../../help/why';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { AuthService } from '../../services/auth.service';
import { FixedAssetSettingsService } from '../../services/fixedAssetSettings.service';

const monthLabel = (iso: string) =>
  new Date(`${iso.slice(0, 7)}-01T00:00:00`).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

interface Props {
  open: boolean;
  asset: FixedAsset;
  onClose: () => void;
  onDisposed: () => void;
}

const today = () => toLocalIsoDate();
const money = (n: number) =>
  (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const DisposeAssetDialog: React.FC<Props> = ({ open, asset, onClose, onDisposed }) => {
  const toast = useToast();
  const [disposalDate, setDisposalDate] = useState(today());
  const [proceeds, setProceeds] = useState('0');
  const [cashAccountId, setCashAccountId] = useState('');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [saving, setSaving] = useState(false);
  const canChooseAccount = usePermission(PERMISSIONS.FIXED_ASSET_SETTINGS_MANAGE);
  const [defaultFundingId, setDefaultFundingId] = useState('');

  useEffect(() => {
    if (!open) return;
    AccountService.getLookup()
      .then(res => setAccounts(res ?? []))
      .catch(e => toast.showApiError(e, 'Failed to load accounts'));
    const institutionId = AuthService.getSelectedInstitution();
    if (institutionId) {
      FixedAssetSettingsService.getByInstitution(institutionId)
        .then(s => setDefaultFundingId(s?.defaultFundingAccountId ?? ''))
        .catch(() => setDefaultFundingId(''));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const cashAccounts = leafAccounts(accounts).filter(a => a.accountType === 'ASSET');

  const { nbv, gain } = useMemo(() => {
    const p = parseFloat(proceeds) || 0;
    const n = assetCarrying(asset);
    return { nbv: n, gain: p - n };
  }, [proceeds, asset]);

  // The server refuses a disposal in a month that is already depreciated (that month would need reversing first)
  const throughKey = asset.depreciatedThrough?.slice(0, 7);
  const dateTooEarly = !!throughKey && !!disposalDate && disposalDate.slice(0, 7) <= throughKey;
  const zeroProceeds = !(parseFloat(proceeds) > 0);
  // Without the accounts key the server uses the default funding account; a write-off needs no account at all
  const needsAccount = canChooseAccount ? !cashAccountId : !zeroProceeds && !defaultFundingId;
  const defaultFundingName = accounts.find(a => a.id === defaultFundingId)?.name;

  const submit = async () => {
    try {
      setSaving(true);
      await FixedAssetService.dispose(asset.id, {
        disposalDate,
        proceeds: parseFloat(proceeds) || 0,
        cashOrReceivableAccountId: canChooseAccount ? cashAccountId : undefined,
      });
      toast.showSuccess('Asset disposed');
      onDisposed();
    } catch (e) {
      toast.showApiError(e, 'Failed to dispose asset');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog data-help-page="fixedAssets.disposeDialog.page" open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Dispose {asset.assetCode}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <TextField data-help="fixedAssets.disposeDialog.field.date" label="Disposal date" type="date" InputLabelProps={{ shrink: true }}
          value={disposalDate} onChange={e => setDisposalDate(e.target.value)} fullWidth />
        <TextField data-help="fixedAssets.disposeDialog.field.proceeds" label="Proceeds" type="number" value={proceeds}
          onChange={e => setProceeds(e.target.value)} fullWidth helperText="0 = write-off" />
        {!canChooseAccount && !zeroProceeds && (
          defaultFundingId ? (
            <Alert severity="info" data-testid="proceeds-account-default">
              Proceeds go to {defaultFundingName ?? 'the default funding account'} (set by your accountant).
            </Alert>
          ) : (
            <Alert severity="warning" data-testid="proceeds-account-missing">
              Ask the accountant to set the default funding account in Fixed asset settings before disposing with proceeds.
            </Alert>
          )
        )}
        {canChooseAccount && <AccountAutocomplete data-help="fixedAssets.disposeDialog.field.account"
          accounts={cashAccounts}
          value={cashAccountId}
          onChange={setCashAccountId}
          label="Proceeds account"
          required
          placeholder="Select"
          helperText="Cash / receivable account debited for the proceeds"
        />}

        {dateTooEarly && (
          <Alert severity="warning">
            Depreciation is already posted through {monthLabel(asset.depreciatedThrough!)}, so the disposal date must be in a
            later month.
          </Alert>
        )}
        <Alert severity={gain >= 0 ? 'success' : 'error'}>
          <Box>NBV {money(nbv)} · Proceeds {money(parseFloat(proceeds) || 0)} ·{' '}
            {gain >= 0 ? <b>Gain {money(gain)}</b> : <b>Loss {money(-gain)}</b>}
          </Box>
        </Alert>
        <Typography variant="caption" color="text.secondary">
          Any missing depreciation up to the month before the disposal month is posted first, then the gain or loss is
          worked out, so the final figure may differ slightly.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button data-help="fixedAssets.disposeDialog.cancel" onClick={onClose}>Cancel</Button>
        <Button data-help="fixedAssets.disposeDialog.save" variant="contained" onClick={submit} {...helpWhy(dateTooEarly ? { kind: 'custom', message: 'The disposal date is in a month that is already depreciated. Pick a date in a later month.' } : needsAccount ? { kind: 'custom', message: canChooseAccount ? 'Pick the proceeds account (for a write-off too).' : 'Ask the accountant to set the default funding account in Fixed asset settings.' } : null)} disabled={dateTooEarly || needsAccount || saving}>Dispose</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DisposeAssetDialog;
